import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type EventUser, type Item, type Participant } from "@/lib/api";
import { recentEventsStorage } from "@/lib/storage";

function userPath(shortcut: string, userKey: string): string {
  return `/api/v1/events/${shortcut}/user/${userKey}`;
}

export function useUserEvent(shortcut: string, userKey: string) {
  return useQuery({
    queryKey: ["event-user", shortcut, userKey],
    queryFn: () => api.get<EventUser>(userPath(shortcut, userKey)),
    enabled: Boolean(shortcut && userKey),
  });
}

function useInvalidate(shortcut: string, userKey: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["event-user", shortcut, userKey] });
}

export function useUpdateSelf(shortcut: string, userKey: string) {
  const invalidate = useInvalidate(shortcut, userKey);
  return useMutation({
    mutationFn: (payload: { name?: string; participates?: boolean; persons?: number }) =>
      api.put<Participant>(`${userPath(shortcut, userKey)}/participant`, payload),
    onSuccess: () => invalidate(),
  });
}

export function useAddUserItem(shortcut: string, userKey: string) {
  const invalidate = useInvalidate(shortcut, userKey);
  return useMutation({
    mutationFn: (payload: { name: string; comment?: string | null }) =>
      api.post<Item>(`${userPath(shortcut, userKey)}/items`, payload),
    onSuccess: () => invalidate(),
  });
}

export function useClaimItem(shortcut: string, userKey: string) {
  const invalidate = useInvalidate(shortcut, userKey);
  return useMutation({
    mutationFn: (itemId: number) =>
      api.post<Item>(`${userPath(shortcut, userKey)}/items/${itemId}/claim`),
    onSuccess: () => invalidate(),
  });
}

export function useReleaseItem(shortcut: string, userKey: string) {
  const invalidate = useInvalidate(shortcut, userKey);
  return useMutation({
    mutationFn: (itemId: number) =>
      api.post<Item>(`${userPath(shortcut, userKey)}/items/${itemId}/release`),
    onSuccess: () => invalidate(),
  });
}

export function useSetUserComment(shortcut: string, userKey: string) {
  const invalidate = useInvalidate(shortcut, userKey);
  return useMutation({
    mutationFn: ({ itemId, comment }: { itemId: number; comment: string | null }) =>
      api.put<Item>(`${userPath(shortcut, userKey)}/items/${itemId}/comment`, {
        userComment: comment,
      }),
    onSuccess: () => invalidate(),
  });
}

export function useRememberAsUser() {
  return (event: EventUser, userKey: string) => {
    recentEventsStorage.upsert({
      shortcut: event.shortcut,
      key: userKey,
      role: "user",
      name: event.name,
      eventAt: event.eventAt,
    });
  };
}
